import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.product.$urlSegment';
import { useTrackProductProfileClick } from '../hooks/use-track-product-profile-click';
import {
    CONTENT_CAP_PADDED,
    DetailBylineRow,
} from '~/ui-rocco/pages/detail-shared';

/**
 * F2 — product title (Figma 134:12983).
 *
 * The title runs from the page gutter at 80px on a 68px line box in the text
 * cut, and under
 * it a byline: who makes the thing, then where they are. The eyebrow that used
 * to sit above the title is gone — the supplier is named beneath it now, once.
 *
 * Its own section rather than the shared `DetailHero`: that hero indents its
 * title by 28.8rem to clear the header logomark and carries an eyebrow above
 * it, and this design does neither. The project and professional pages still
 * want it as it is.
 */
export const ProductHeroSection = () => {
    const { product } = useLoaderData<typeof loader>();
    const trackProfileClick = useTrackProductProfileClick();
    const professional = product.Professional;
    const supplier = professional?.Title ?? product.Brand ?? '';

    // Where the supplier is, as they list it: the city, and the country it
    // sits in. Either half may be missing, so the comma is only earned when
    // both are there.
    const site = professional?.LibraryLocations?.[0];
    const place = [site?.City || site?.Region, site?.CountryName]
        .filter(Boolean)
        .join(', ');

    return (
        <section className="bg-ground max-md:px-6 max-md:pt-8 md:px-[3rem] md:pt-[0.6rem]">
            <div className={CONTENT_CAP_PADDED}>
                <h1 className="m-0 font-sans font-medium tracking-[-0.03em] text-[#323232] max-md:mt-4 max-md:text-[2.25rem] max-md:leading-[1.05] md:mt-[3.75rem] md:-ml-[0.5rem] md:text-[5rem] md:leading-[4.25rem]">
                    {product.Title}
                </h1>

                {/* One row 48px under the title: Back at the gutter, the
                    byline in a 51rem track from it, and the place ranged
                    right to the far gutter. */}
                <DetailBylineRow
                    backFallbackLink={
                        product.Categories?.[0]?.Link ?? '/products'
                    }
                    professional={
                        supplier
                            ? {
                                  title: supplier,
                                  link: professional?.Link ?? undefined,
                                  onClick: trackProfileClick,
                              }
                            : undefined
                    }
                    trailing={place || undefined}
                />
            </div>
        </section>
    );
};
